// Phase C: pre-launch seat claims route through the new REST endpoint
// `/api/lobby/<uuid>/claim_seat/` (gameApi.claimLobbySeat). Post-launch
// (in-progress join / reclaim) keeps the existing Sit-or-Join branch.
//
// Returns one of:
//   'claim_seat'   — pre-launch lobby-aware seat claim
//   'sit'          — reclaim an existing GamePlayer
//   'join'         — create a new GamePlayer
//   'noop'         — pre-launch with no lobby context to write to
//
// Extracted from Game.js so the Jest suite can exercise this
// branch without pulling in the swiper-laden ArtificialPlayer
// import chain that Game.js transitively loads.
import { isSnowflakeUsername } from '../../hooks/useLocalDisplayName';

/**
 * Pick the per-seat platform display name to capture into
 * `Seat.platform_display_name`. Discord precedence mirrors the
 * backend's: per-server nick wins, global display name is next-best,
 * the @handle (provider_username) is the last Discord-derived option.
 *
 * Any candidate matching the synthetic `discord_<snowflake>` username
 * is skipped (belt-and-suspenders against a malformed identity
 * payload), so the snowflake can never land in the captured nick.
 * Open-web / unauthenticated callers fall through to '' and the
 * backend serializer renders from the account fields.
 *
 * @param {object|null} discord Discord context slice.
 * @returns {string} the captured nick, or '' when none survives.
 */
export function pickPlatformDisplayName(discord) {
    if (!discord) {
        return '';
    }
    const candidates = [
        discord.discordNick,
        discord.discordGlobalName,
        discord.discordUsername,
    ];
    for (const candidate of candidates) {
        if (candidate && !isSnowflakeUsername(candidate)) {
            return candidate;
        }
    }
    return '';
}

export function dispatchTakeSeat({
    seat, lobbyData, gameData, gameApi, groupHandler, discord,
}) {
    if (!gameData || !gameData.id) {
        const lobbyId = lobbyData && lobbyData.lobby_id;
        if (lobbyId) {
            const nick = pickPlatformDisplayName(discord);
            gameApi.claimLobbySeat(lobbyId, seat, nick);
            return 'claim_seat';
        }
        return 'noop';
    }
    const gameplayers = gameData.gameplayer_set || [];
    const existing = gameplayers.find(gp => gp && gp.seat === seat);
    if (existing) {
        gameApi.sit(seat);
        return 'sit';
    }
    // Empty seat on an existing Game -> create a new GamePlayer.
    // The Join sender lives on gameApi (the per-seat path every seated
    // flow already uses); it used to hang off app.gameController, which
    // is null in production, so this branch silently fell through to
    // sit() and the backend raised GamePlayer.DoesNotExist.
    gameApi.createNewGamePlayer(seat);
    return 'join';
}
